import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  CircularProgress,
  Typography,
} from '@material-ui/core';

import {
  AutoCompleteAddress,
  AutoCompleteAddresses,
  Nullable,
} from '@/core/interfaces/common';

import { colors } from '@/utils/constants';
import {
  findLongestCommonSubstring,
  getFormattedAddress,
} from '@/utils/helpers';

import { StyledTextField as StyledTextFieldBase } from '@/themes/globalStyles';

const StyledTextField = styled(StyledTextFieldBase)`
  padding-right: 30px;
`;

interface AddressAutocompleteProps {
  addressSuggestions: AutoCompleteAddresses;
  autoComplete: string;
  color?: string;
  fetchAddressSuggestions: (inputValue: string, params: { selected?: string }) => void;
  isLoading: boolean;
  setZipCode: (name: string) => void;
  onChange: (event: Nullable<string | AutoCompleteAddress>) => void;
  value: Nullable<string>;
}

export const AddressAutocomplete = ({
  addressSuggestions,
  color,
  fetchAddressSuggestions,
  isLoading,
  setZipCode,
  onChange,
  value,
}: AddressAutocompleteProps) => {
  const autocompleteRef = useRef<Element>(null);

  const [
    inputValue,
    setInputValue,
  ] = useState('');
  const [
    isEntriesSelected,
    setIsEntriesSelected,
  ] = useState(false);
  const [
    hasFocus,
    setHasFocus,
  ] = useState(false);

  const debouncedFetchAddressSuggestions = useCallback(
    debounce(newInputValue => {
      fetchAddressSuggestions(newInputValue, {});
    }, 500),
    []
  );

  useEffect(() => {
    if (inputValue && !isEntriesSelected) {
      debouncedFetchAddressSuggestions(inputValue);
    }
  }, [
    inputValue,
    isEntriesSelected,
  ]);

  useEffect(() => {
    if (autocompleteRef?.current) {
      const inputElement = autocompleteRef?.current?.querySelector('input');

      if (inputElement) {
        inputElement.setAttribute('autocomplete', 'off');
      }
    }
  }, [autocompleteRef]);

  const handleAutocompleteChange = (
    _event: React.ChangeEvent<{ [key: string]: never }>,
    newValue: Nullable<string | AutoCompleteAddress>
  ) => {
    if (typeof newValue === 'string') {
      onChange(newValue);
      setIsEntriesSelected(false);

      return;
    }

    if (newValue?.entries && newValue?.entries > 1) {
      const selected = `${newValue.streetLine} ${newValue.secondary} (${newValue.entries}) ${newValue.city} ${newValue.state} ${newValue.zipcode}`;

      setInputValue(`${newValue.streetLine} ${newValue.secondary}`);

      setIsEntriesSelected(true);

      fetchAddressSuggestions(inputValue, { selected });
    } else {
      if (autocompleteRef?.current) {
        const inputElement = autocompleteRef?.current?.querySelector('input');

        if (inputElement) {
          inputElement.blur();
        }
      }
      onChange(newValue);
      setIsEntriesSelected(false);

      if (newValue?.zipcode) {
        setZipCode(newValue.zipcode);
      } else {
        setZipCode('');
      }
    }
  };

  return (
    <Autocomplete
      ref={autocompleteRef}
      getOptionLabel={option => getFormattedAddress(option, true)}
      options={addressSuggestions}
      loading={isLoading}
      filterOptions={(options, state) => (
        options
          .filter(option => (
            !isEntriesSelected ||
            `${option.streetLine} ${option.secondary}`?.includes(state.inputValue)
          ))
      )}
      fullWidth
      autoComplete
      autoSelect
      freeSolo
      includeInputInList
      blurOnSelect={false}
      getOptionSelected={(option, selectedValue: string | AutoCompleteAddress) => (
        option.streetLine === selectedValue || option === selectedValue
      )}
      value={value}
      open={hasFocus}
      onChange={handleAutocompleteChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      renderInput={params => (
        <StyledTextField
          {...params}
          fullWidth
          label="Street Address"
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ?
                  (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) :
                  null}
                {params.InputProps.endAdornment}
              </>
            ),
            name: 'streetAddress',
            style: {
              color: color || colors.black,
            },
          }}
          InputLabelProps={{
            style: {
              color: color || 'rgba(0, 0, 0, 0.54)',
            },
          }}
        />
      )}
      renderOption={option => {
        const wholeAddress = getFormattedAddress(option);
        const [
          commonPart,
          restOfTheString,
        ] = findLongestCommonSubstring(inputValue, wholeAddress);

        return (
          <Typography
            variant="body1"
            data-testid="address-suggestion"
          >
            {commonPart}
            <strong>
              {restOfTheString}
            </strong>
          </Typography>
        );
      }}
    />
  );
};

AddressAutocomplete.defaultProps = {
  color: null,
};
